/**
 * Strategic Relationship 360 — vue générique (aucun code spécifique dossier).
 * Sections : carte de relations, sociétés, projets/commodities, financements,
 * deals indépendants, documents, intelligence externe, questions ouvertes, actions.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Handshake, Loader2, Building2, Users, Briefcase, FileText, Radar, HelpCircle,
  ListTodo, Network, Gem, Landmark, ChevronLeft, ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { fmtError } from "@/lib/notify";

const fmtAmt = (n) => (typeof n === "number" ? n.toLocaleString("fr-CH") : n);

function Section({ icon: Icon, title, count, children, testid }) {
  return (
    <section className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4" data-testid={testid}>
      <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#43C6D8]" /> {title}
        {count !== undefined && <span className="text-[10px] text-zinc-500 font-normal">({count})</span>}
      </h2>
      {children}
    </section>
  );
}

const Prov = ({ p }) => {
  const txt = typeof p === "string" ? p : p?.evidence || p?.source || "";
  if (!txt) return null;
  const declared = /DECLARED|CONFIRM/i.test(txt);
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${declared ? "border-[#EF9F27]/40 text-[#EF9F27]" : "border-zinc-700 text-zinc-500"}`}>
      {declared ? "DECLARED" : "PUBLIC"}
    </span>
  );
};

export default function StrategicRelationship360() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/workspace/relationships/strategic/${id}/360`)
      .then((r) => setData(r.data))
      .catch((e) => setError(fmtError(e, "Indisponible")));
  }, [id]);

  if (error) return <div className="text-red-400 text-xs" data-testid="sr360-error">{error}</div>;
  if (!data) {
    return (
      <div className="text-zinc-500 text-sm py-8 text-center" data-testid="sr360-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> …
      </div>
    );
  }

  const { root, persons, companies, relationship_map, projects, commodities, financings,
    deals, documents, signals, open_questions, next_actions, watchlisted_entities } = data;

  return (
    <div className="space-y-4" data-testid="sr360-page">
      <button onClick={() => navigate("/app/relations")} className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1" data-testid="sr360-back-btn">
        <ChevronLeft className="w-3 h-3" /> {t("sr360.title", { defaultValue: "Relations stratégiques" })}
      </button>

      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100 flex items-center gap-2" data-testid="sr360-root-name">
            <Handshake className="w-5 h-5 text-[#43C6D8]" /> {root.name}
          </h1>
          <p className="text-zinc-400 text-xs max-w-3xl mt-1">{root.description}</p>
        </div>
        {watchlisted_entities.length > 0 && (
          <span className="text-[10px] text-[#EF9F27] border border-[#EF9F27]/40 rounded px-2 py-1 flex items-center gap-1" data-testid="sr360-watchlist-badge">
            <Radar className="w-3 h-3" /> {t("sr360.radar_on", { defaultValue: "Radar actif" })} : {watchlisted_entities.join(", ")}
          </span>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Section icon={Network} title={t("sr360.map", { defaultValue: "Carte de relations" })} count={relationship_map.length} testid="sr360-map">
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {relationship_map.map((r, i) => (
              <div key={i} className="text-[11px] text-zinc-400 flex items-center gap-1.5 flex-wrap py-0.5 border-b border-zinc-800/60">
                <span className="text-zinc-200">{r.from.label}</span>
                <span className="text-[#43C6D8] font-mono text-[10px]">—{r.rel_type}→</span>
                <span className="text-zinc-200">{r.to.label}</span>
                <Prov p={r.provenance} />
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Building2} title={t("sr360.companies_title", { defaultValue: "Personnes & sociétés" })} count={persons.length + companies.length} testid="sr360-companies">
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {persons.map((p) => (
              <div key={p.id} className="text-[11px] py-0.5 flex items-center gap-2 border-b border-zinc-800/60">
                <Users className="w-3 h-3 text-[#EF9F27]" />
                <span className="text-zinc-200">{p.name}</span>
                <span className="text-zinc-500">{p.role}</span>
              </div>
            ))}
            {companies.map((c) => (
              <div key={c.id} className="text-[11px] py-0.5 flex items-center gap-2 border-b border-zinc-800/60">
                <Building2 className="w-3 h-3 text-zinc-500" />
                <span className="text-zinc-200">{c.name}</span>
                <span className="text-zinc-500 truncate">{c.sector}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Gem} title={t("sr360.assets", { defaultValue: "Projets & matières premières" })} count={projects.length + commodities.length} testid="sr360-assets">
          <div className="space-y-1.5">
            {projects.map((p) => (
              <div key={p.id} className="text-[11px] text-zinc-300">
                <span className="text-zinc-100 font-medium">{p.label}</span>
                <div className="text-zinc-500">{p.description}</div>
              </div>
            ))}
            {commodities.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {commodities.map((c) => (
                  <span key={c.id} className={`text-[10px] px-2 py-0.5 rounded border ${c.maturity === "TO_CONFIRM" ? "border-[#EF9F27]/40 text-[#EF9F27]" : "border-zinc-700 text-zinc-300"}`} title={c.description}>
                    {c.label.replace(/^.*—\s*/, "")} · {c.maturity}
                  </span>
                ))}
              </div>
            )}
          </div>
        </Section>

        <Section icon={Landmark} title={t("sr360.financings", { defaultValue: "Financements identifiés" })} count={financings.length} testid="sr360-financings">
          <div className="space-y-2">
            {financings.map((f) => (
              <div key={f.id} className="text-[11px]">
                <div className="text-zinc-100 font-medium">{f.label}</div>
                <div className="text-zinc-500">{f.description}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section icon={Briefcase} title={t("sr360.deals", { defaultValue: "Deals indépendants" })} count={deals.length} testid="sr360-deals">
        <div className="grid md:grid-cols-2 gap-3">
          {deals.map((d) => (
            <div key={d.id} className="border border-zinc-800 rounded-lg p-3" data-testid={`sr360-deal-${d.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm text-zinc-100 font-medium">{d.title}</div>
                <button onClick={() => navigate(`/app/deals/${d.id}`)} className="text-zinc-500 hover:text-[#43C6D8]" data-testid={`sr360-deal-open-${d.id}`}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">{d.asset_class} · {d.statut} · {d.counterparty}</div>
              {d.deal_score?.status === "done" && (
                <div className="mt-2 border border-zinc-800 rounded p-2" data-testid={`sr360-deal-score-${d.id}`}>
                  <div className="flex items-center gap-2">
                    {d.deal_score.rating?.grade && (
                      <span className="text-[11px] font-mono font-bold px-1.5 py-0.5 rounded border border-zinc-700 text-zinc-100" data-testid={`sr360-deal-grade-${d.id}`}>
                        {d.deal_score.rating.grade}{d.deal_score.rating.projected_grade && d.deal_score.rating.projected_grade !== d.deal_score.rating.grade ? ` → ${d.deal_score.rating.projected_grade}` : ""}
                      </span>
                    )}
                    <span className={`text-sm font-bold ${d.deal_score.global >= 60 ? "text-[#1D9E75]" : d.deal_score.global >= 40 ? "text-[#EF9F27]" : "text-red-400"}`}>
                      {d.deal_score.global}/100
                    </span>
                    {d.deal_score.projected?.global > d.deal_score.global && (
                      <span className="text-[11px] font-semibold text-[#43C6D8]" data-testid={`sr360-deal-projected-${d.id}`}>
                        → {d.deal_score.projected.global}/100 {t("sr360.projected", { defaultValue: "si dossier complété" })}
                      </span>
                    )}
                    <span className="text-[10px] text-zinc-400 italic leading-snug">{d.deal_score.verdict}</span>
                  </div>
                  {(d.deal_score.economics?.value_potential_chf > 0 || d.deal_score.economics?.inaction_cost_chf_monthly > 0) && (
                    <div className="flex items-center gap-3 mt-1.5 text-[10px]" data-testid={`sr360-deal-economics-${d.id}`}>
                      {d.deal_score.economics.value_potential_chf > 0 && (
                        <span className="text-[#1D9E75]">≈ CHF {Math.round(d.deal_score.economics.value_potential_chf).toLocaleString("fr-CH")} {t("sr360.value_pot", { defaultValue: "de valeur potentielle" })}</span>
                      )}
                      {d.deal_score.economics.inaction_cost_chf_monthly > 0 && (
                        <span className="text-red-400">− CHF {Math.round(d.deal_score.economics.inaction_cost_chf_monthly).toLocaleString("fr-CH")}/{t("sr360.month", { defaultValue: "mois d'inaction" })}</span>
                      )}
                    </div>
                  )}
                  {(d.deal_score.actions || []).length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      {d.deal_score.actions.slice(0, 3).map((a, i) => (
                        <div key={i} className="text-[10px] text-zinc-400">
                          <span className="text-[#43C6D8]">↑</span> {a.action}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {d.financing_profile?.facilities?.map((f) => (
                <div key={f.facility_id} className="text-[10px] text-zinc-400 mt-1.5 border-t border-zinc-800/60 pt-1.5">
                  <span className="text-zinc-200">{f.borrower}</span> · {f.year} · {fmtAmt(f.original_amount)} {f.original_currency}
                  <span className="text-zinc-500"> → EUR {fmtAmt(f.amount_eur)} → CHF {fmtAmt(f.amount_chf)}</span>
                  <span className="text-[#EF9F27]"> · outstanding : {f.outstanding_amount}</span>
                </div>
              ))}
              {d.commodity_portfolio?.items && (
                <div className="flex flex-wrap gap-1 mt-1.5 border-t border-zinc-800/60 pt-1.5">
                  {d.commodity_portfolio.items.map((c) => (
                    <span key={c.commodity} className={`text-[9px] px-1.5 py-0.5 rounded border ${/CONFIRM/.test(c.production_status) ? "border-[#EF9F27]/40 text-[#EF9F27]" : "border-zinc-700 text-zinc-400"}`}>
                      {c.commodity} · {c.production_status.split(" ")[0]}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      <div className="grid lg:grid-cols-2 gap-4">
        <Section icon={Radar} title={t("sr360.intel", { defaultValue: "Intelligence externe" })} count={signals.length} testid="sr360-signals">
          {signals.length === 0 && <div className="text-[11px] text-zinc-500">{t("sr360.no_signals", { defaultValue: "Aucun signal encore. Les entités en watchlist seront scannées au prochain cycle Radar." })}</div>}
          <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
            {signals.map((s) => (
              <a key={s.id} href={s.url} target="_blank" rel="noreferrer" className="block text-[11px] text-zinc-300 hover:text-[#43C6D8] py-0.5 border-b border-zinc-800/60">
                <span className="text-[9px] text-[#EF9F27] mr-1.5">{s.criticality}</span>
                {s.title}
                {s.entity_name && <span className="text-zinc-600"> · {s.entity_name}</span>}
              </a>
            ))}
          </div>
        </Section>

        <Section icon={FileText} title={t("sr360.documents", { defaultValue: "Documents" })} count={documents.length} testid="sr360-documents">
          {documents.length === 0 && <div className="text-[11px] text-zinc-500">{t("sr360.no_docs", { defaultValue: "Aucun document lié. Les documents reçus passeront le dossier en DOCUMENT_VERIFIED." })}</div>}
          <div className="space-y-1">
            {documents.map((doc) => (
              <div key={doc.id} className="text-[11px] text-zinc-300 py-0.5 border-b border-zinc-800/60">{doc.name}</div>
            ))}
          </div>
        </Section>

        <Section icon={HelpCircle} title={t("sr360.questions", { defaultValue: "Questions ouvertes" })} count={open_questions.length} testid="sr360-questions">
          <ol className="space-y-1 list-decimal list-inside">
            {open_questions.map((q, i) => (
              <li key={i} className="text-[11px] text-zinc-300">{q}</li>
            ))}
          </ol>
        </Section>

        <Section icon={ListTodo} title={t("sr360.actions", { defaultValue: "Prochaines actions" })} count={next_actions.length} testid="sr360-actions">
          <div className="space-y-1">
            {next_actions.map((a) => (
              <div key={a.deal_id} className="text-[11px] text-zinc-300 py-0.5 border-b border-zinc-800/60">
                <span className="text-zinc-100">{a.deal_title}</span>
                <span className="text-zinc-500"> · {a.statut}</span>
                {a.next_step && <span className="text-zinc-400"> → {a.next_step}</span>}
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
